import { useEffect, useState } from "react";
import {
  fetchTenantConfig,
  type TenantConfig,
} from "../services/globalConfigService";
import { useAcTenantId } from "./useAcTenantId";

export interface UseGlobalConfigResult {
  config: TenantConfig | null;
  loading: boolean;
  error: string | null;
}

let cachedConfig: TenantConfig | null = null;
let cachedTenantKey: string | null = null;
let inFlight: Promise<TenantConfig | null> | null = null;
let inFlightTenantKey: string | null = null;

async function loadConfigOnce(
  tenantKey: string,
): Promise<TenantConfig | null> {
  if (cachedConfig && cachedTenantKey === tenantKey) return cachedConfig;
  if (inFlight !== null && inFlightTenantKey === tenantKey) return inFlight;

  inFlightTenantKey = tenantKey;
  inFlight = fetchTenantConfig()
    .then((res) => {
      cachedConfig = res?.data ?? null;
      cachedTenantKey = tenantKey;
      return cachedConfig;
    })
    .catch((err: unknown) => {
      if (cachedTenantKey === tenantKey) {
        cachedConfig = null;
        cachedTenantKey = null;
      }
      const msg = err instanceof Error ? err.message : "Failed to load config";
      throw new Error(msg);
    })
    .finally(() => {
      inFlight = null;
      inFlightTenantKey = null;
    });

  return inFlight;
}

/**
 * Loads GET /admin-flags once per Assessment Center tenant (deduped).
 * Mounted from app bootstrap so the request fires on every route.
 */
export function useGlobalConfig(): UseGlobalConfigResult {
  const acTenantId = useAcTenantId();
  const tenantKey = acTenantId || "no-ac-tenant";
  const cacheHit = cachedConfig !== null && cachedTenantKey === tenantKey;

  const [config, setConfig] = useState<TenantConfig | null>(
    cacheHit ? cachedConfig : null,
  );
  const [loading, setLoading] = useState<boolean>(!cacheHit);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    if (cachedConfig && cachedTenantKey === tenantKey) {
      setConfig(cachedConfig);
      setLoading(false);
      setError(null);
      return () => {
        mounted = false;
      };
    }

    setLoading(true);
    setError(null);
    loadConfigOnce(tenantKey)
      .then((c) => {
        if (!mounted) return;
        setConfig(c);
      })
      .catch((e: unknown) => {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : "Failed to load config");
        setConfig(null);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [tenantKey]);

  return { config, loading, error };
}

export function __resetGlobalConfigCacheForTests(): void {
  cachedConfig = null;
  cachedTenantKey = null;
  inFlight = null;
  inFlightTenantKey = null;
}
